import { Box, Radio, Typography } from "@mui/material";

import type { AspectRatio } from "@/types";
import { getAcceptsCustomSize } from "@/utils/customSize";

import { InferenceStyleFragment } from "../../inference/queries.graphql";
import type { ReferenceType } from "../References/types";
import {
  getAspectRatioPresetsFromReferences,
  getReferencesWithUnsupportedAspectRatio,
} from "../References/utils";
import { BestMatchPresetIcon } from "./components/BestMatchPresetIcon/BestMatchPresetIcon";
import { PresetIcon } from "./components/PresetIcon/PresetIcon";
import { RadioGroupHeader } from "./components/RadioGroupHeader/RadioGroupHeader";
import { RadioItemWrapper } from "./components/RadioItemWrapper/RadioItemWrapper";
import { ASPECT_RATIO_MAP, SORTED_ASPECT_RATIO_TYPES } from "./constants";
import type { DimensionsType, PresetType } from "./types";

export const getClosestPresetToCurrentDimensions = (
  presets: PresetType[],
  currentDimensions: DimensionsType,
) => {
  const sorter = presetSorterByDistanceToCurrentDimensions(currentDimensions);
  return [...presets].sort(sorter)[0];
};

const presetSorterByDistanceToCurrentDimensions = (d: DimensionsType) => {
  const sorter = sorterByDistanceToCurrentDimensions(d);
  return (a: PresetType, b: PresetType) => sorter(a.dimensions, b.dimensions);
};

export const getClosestToCurrentDimensions = (
  dimensions: DimensionsType[],
  currentDimensions: DimensionsType,
) => {
  const sorter = sorterByDistanceToCurrentDimensions(currentDimensions);
  return [...dimensions].sort(sorter)[0];
};

const sorterByDistanceToCurrentDimensions = (target: DimensionsType) => {
  return (a: DimensionsType, b: DimensionsType) => {
    const distanceA = aspectRatioDistanceBetweenDimensions(a, target);
    const distanceB = aspectRatioDistanceBetweenDimensions(b, target);
    const diff = distanceA - distanceB;
    if (Math.abs(diff) < 0.01) {
      return (
        distanceBetweenDimensions(a, target) -
        distanceBetweenDimensions(b, target)
      );
    }
    return diff;
  };
};

export const aspectRatioDistanceBetweenDimensions = (
  a: DimensionsType,
  b: DimensionsType,
) => {
  const aspectRatioA = a[0] / a[1];
  const aspectRatioB = b[0] / b[1];
  return Math.abs(Math.log(aspectRatioA / aspectRatioB));
};

const distanceBetweenDimensions = (a: DimensionsType, b: DimensionsType) => {
  return Math.sqrt(Math.pow(a[0] - b[0], 2) + Math.pow(a[1] - b[1], 2));
};

const getPresetFromAspectRatio = (aspectRatio: AspectRatio): PresetType => ({
  ...ASPECT_RATIO_MAP[aspectRatio.type],
  dimensions: [aspectRatio.width, aspectRatio.height],
  resolution: aspectRatio.videoResolution ?? aspectRatio.imageResolution,
});

export const getPresetsFromAspectRatios = (
  aspectRatios: AspectRatio[],
): PresetType[] =>
  [...aspectRatios]
    .sort(
      (a, b) =>
        SORTED_ASPECT_RATIO_TYPES.indexOf(a.type) -
        SORTED_ASPECT_RATIO_TYPES.indexOf(b.type),
    )
    .map(getPresetFromAspectRatio);

export const getPresetsFromStyleAndReferences = (
  style: Pick<InferenceStyleFragment, "capabilities"> | null,
  references?: ReferenceType[],
) => {
  let stylePresets: PresetType[] = [],
    referencesPresets: PresetType[] = [];
  if (style && style.capabilities) {
    let aspectRatios = style.capabilities.textToOutputAspectRatios ?? [];
    if (
      (references?.length && style.capabilities.imageToOutputAspectRatios) ||
      aspectRatios.length === 0 // using aspect ratios from img to video for models that don't support text to video
    ) {
      aspectRatios = style.capabilities.imageToOutputAspectRatios ?? [];
    }
    if (aspectRatios.length > 0) {
      stylePresets = style.capabilities
        ? getPresetsFromAspectRatios(aspectRatios)
        : [];
    }
    if (getAcceptsCustomSize(style.capabilities) && references) {
      const referencesWithAspectRatioNotInStyle =
        getReferencesWithUnsupportedAspectRatio(references, stylePresets);
      referencesPresets = style?.capabilities
        ? getAspectRatioPresetsFromReferences(
            referencesWithAspectRatioNotInStyle,
            style,
          )
        : [];
    }
  }
  return [...stylePresets, ...referencesPresets];
};

export const findPresetByDimensions = (
  presets: PresetType[],
  dimensions: DimensionsType,
) => presets.find((p) => getAreDimensionsEqual(p.dimensions, dimensions));

export const getAreDimensionsEqual = (
  dimensions1: DimensionsType,
  dimensions2: DimensionsType,
) => dimensions1[0] === dimensions2[0] && dimensions1[1] === dimensions2[1];

export const getAreAspectRatiosEqual = (
  dimensions1: DimensionsType,
  dimensions2: DimensionsType,
) => dimensions1[0] / dimensions1[1] === dimensions2[0] / dimensions2[1];

export const getDimensionsString = (dimensions: DimensionsType) =>
  `${dimensions[0]} x ${dimensions[1]}`;

type GetAspectRatioRadioGroupItemsParams =
  | {
      value: DimensionsType | null;
      onChange: (dimensions: DimensionsType | null) => void;
      presets: PresetType[];
      canSelectNone?: false;
    }
  | {
      value: DimensionsType | null;
      onChange: (dimensions: DimensionsType | null) => void;
      presets: PresetType[];
      canSelectNone?: true;
      noSelectionHeaderLabel?: string;
      noSelectionItemLabel?: string;
    };

export const getAspectRatioRadioGroupItems = (
  props: GetAspectRatioRadioGroupItemsParams,
) => {
  const { value, onChange, presets, canSelectNone } = props;

  const radioGroupItems = [];
  if (canSelectNone) {
    radioGroupItems.push(
      <RadioGroupHeader
        key="header-no-selection"
        label={
          canSelectNone
            ? (props as { noSelectionHeaderLabel?: string })
                .noSelectionHeaderLabel ?? ""
            : ""
        }
      />,
    );
    radioGroupItems.push(
      <RadioItemWrapper
        key="no-selection"
        selected={!value}
        label={
          <Box
            sx={{
              display: "flex",
            }}
          >
            <Typography
              variant="small"
              sx={{
                paddingLeft: "2px",
                paddingRight: "6px",
                color: value ? "secondary.3" : "white",
              }}
            >
              {props.noSelectionItemLabel}
            </Typography>
          </Box>
        }
        control={
          <Radio
            icon={<BestMatchPresetIcon />}
            checkedIcon={<BestMatchPresetIcon />}
            onChange={() => onChange(null)}
          />
        }
      />,
    );
  }
  presets.forEach((preset, ix) => {
    const selected = !!value && getAreDimensionsEqual(value, preset.dimensions);
    if (ix === 0 || presets[ix - 1].label !== preset.label) {
      radioGroupItems.push(
        <RadioGroupHeader
          key={`header-${preset.label}`}
          label={preset.label}
        />,
      );
    }
    radioGroupItems.push(
      <RadioItemWrapper
        key={preset.dimensions.join(",")}
        selected={selected}
        label={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="small"
              sx={{
                fontWeight: "bold",
                paddingLeft: "2px",
              }}
            >
              {preset.aspectRatioLabel || "Custom"}
            </Typography>
            <Typography
              variant="small"
              sx={{
                paddingRight: "6px",
                color: selected ? "white" : "secondary.3",
              }}
            >
              {preset.resolution
                ? `${preset.resolution} · ${getDimensionsString(preset.dimensions)}`
                : getDimensionsString(preset.dimensions)}
            </Typography>
          </Box>
        }
        control={
          <Radio
            data-testid="aspect-ratio-radio"
            data-aspect-ratio-label={preset.label?.toLowerCase()}
            data-aspect-ratio-width={preset.dimensions[0]}
            data-aspect-ratio-height={preset.dimensions[1]}
            data-aspect-ratio-title={
              preset.aspectRatioLabel?.toLowerCase() || "custom"
            }
            onChange={() => onChange(preset.dimensions)}
            icon={<PresetIcon preset={preset} />}
            checkedIcon={<PresetIcon preset={preset} />}
          />
        }
      />,
    );
  });
  return radioGroupItems;
};
